import { Suspense } from 'react'
import { Outlet, useLocation } from 'react-router'
import { IconTile } from '@/components/brand/icon-tile'
import { Logo } from '@/components/brand/logo'
import { Scene, SoundBars, type SceneTone } from '@/components/brand/scene'
import { ThemeToggle } from '@/components/theme/theme-toggle'
import { Skeleton } from '@/components/ui/skeleton'
import { Icon, type IconName } from '@/components/icons/icon'
import { useDocumentTitle } from '@/hooks/use-document-title'

/**
 * The sky moves with the person: dusk as they sign up, night while they prove
 * the address. Claims stay inside what v1 does. Onboarding has its own layout
 * (onboarding-layout.tsx).
 */
const SCENES: Record<string, { page: string; tone: SceneTone; title: string; lede: string }> = {
  '/login': {
    page: 'Sign in',
    tone: 'night',
    title: 'Every call answered, even after hours.',
    lede: 'See who rang, what was booked and which calls need you.',
  },
  '/signup': {
    page: 'Create your account',
    tone: 'dusk',
    title: 'Never lose another job to a missed call.',
    lede: 'RingOut answers your business phone, books jobs into your calendar and hands off to you when it can’t finish.',
  },
  '/verify-email': {
    page: 'Verify your email',
    tone: 'night',
    title: 'One quick check, then you’re in.',
    lede: 'Enter the code from your email to prove the address is yours.',
  },
}

export function AuthLayout() {
  const { pathname } = useLocation()
  const scene = SCENES[pathname] ?? SCENES['/login']
  useDocumentTitle(scene.page)

  return (
    <div className="relative min-h-svh bg-background">
      <div aria-hidden="true" className="duo-line absolute inset-x-0 top-0 z-50" />
      <div className="grid min-h-svh grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)]">
        <aside className="hidden p-3 pt-[15px] lg:block">
          <Scene
            tone={scene.tone}
            className="sticky top-[15px] flex h-[calc(100svh-30px)] min-h-[600px] flex-col rounded-4xl p-10 shadow-card-lift xl:p-12"
          >
            <Logo onDark className="h-9 self-start" />
            <div className="mt-[clamp(2rem,9vh,6rem)] max-w-[30rem]">
              {/* Not a heading: the form's title is the page's h1, and this comes first. */}
              <p className="font-heading text-display font-extrabold tracking-[-0.028em] text-balance text-white">
                {scene.title}
              </p>
              <p className="mt-4 max-w-[44ch] text-lede text-[rgb(234_242_249/0.86)]">{scene.lede}</p>
            </div>
            <ProductMoment />
          </Scene>
        </aside>

        <div className="flex min-w-0 flex-col pt-[3px]">
          <header className="flex items-center justify-between gap-3 px-4 py-4 sm:px-8 sm:py-5">
            <span className="lg:hidden">
              <Logo className="h-8" />
            </span>
            <a
              href="https://ringout.ai"
              className="hidden items-center gap-2 rounded-[9px] px-2 py-2 text-small font-medium text-muted-foreground transition-colors hover:text-foreground lg:inline-flex"
            >
              <Icon name="left" className="size-4" />
              ringout.ai
            </a>
            <ThemeToggle />
          </header>
          <main className="flex flex-1 items-start justify-center px-4 pt-2 pb-16 sm:px-8 lg:items-center lg:pt-0">
            <div className="w-full max-w-[480px]">
              <Suspense fallback={<Skeleton className="h-[480px] w-full rounded-[22px]" />}>
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}

/** Glass cards over the sky: the product at work, with made-up example data. */
function ProductMoment() {
  return (
    <figure className="mt-auto grid max-w-[340px] gap-2.5 self-end pt-10 [@media(max-height:760px)]:hidden">
      <div className="glass flex items-center gap-3 px-4 py-3.5">
        <span className="grid size-10 flex-none place-items-center rounded-full bg-(image:--sweep) font-heading text-small font-extrabold text-white">
          SM
        </span>
        <span className="min-w-0 flex-1">
          <b className="block text-small font-semibold">Sarah Mitchell</b>
          <span className="glass-muted block text-micro">Active call</span>
        </span>
        <SoundBars />
        <span className="glass-muted font-mono text-caption">01:12</span>
      </div>
      <Note icon="cal" title="Booked in Google Calendar" body="Mon 8:00 am, 14 Keele St. Read back to the caller." />
      <Note icon="transfer" warm title="Urgent: transferred" body="Burst main, put through to your mobile." />
      <figcaption className="sr-only">Illustration of RingOut at work, with example data.</figcaption>
    </figure>
  )
}

function Note({ icon, warm, title, body }: { icon: IconName; warm?: boolean; title: string; body: string }) {
  return (
    <div className="glass flex items-start gap-3 px-3.5 py-3">
      <IconTile icon={icon} tone={warm ? 'warm' : 'cool'} size="sm" />
      <span className="min-w-0 flex-1">
        <b className="block text-caption font-semibold">{title}</b>
        <span className="glass-muted block text-micro">{body}</span>
      </span>
    </div>
  )
}
